import { useMutation, useQuery } from '@tanstack/react-query';
import { BIS_BLOCK_LABELS, BIS_REF_FINDING_LABELS, BIS_SECTION_LABELS, BIS_SECTIONS, type BisSection, type BisRefFinding, type BisRefFindingKind, type BisRefMessage, type BisRefStatus } from '@raro/domain';
import { AskField, Button, cx, Label, Notice, Serif, Tag, Textarea, Thinking, Turn } from '@raro/ui';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { BlockEditor, draftOf, sendBlock, type BlockDraft } from './BlockEditor';
import { BlockView } from './BlockView';
import type { BisItem } from './types';

type Target = BisItem;
type Finding = BisRefFinding & { decidedByName: string | null; target: Target | null };
type Ref = {
  id: string; name: string; kind: string; status: BisRefStatus; error: string | null; summary: string | null; truncated: boolean; read: boolean; createdAt: string; by: string | null;
  findings: Finding[]; messages: (BisRefMessage & { byName: string | null })[];
  open: number; diverges: number; applied: number; confirms: number;
};
export type RefRow = { id: string; name: string; kind: string; status: BisRefStatus; error: string | null; summary: string | null; open: number; diverges: number; applied: number; waiting: boolean };

const KIND_ORDER: BisRefFindingKind[] = ['conflict', 'diverges', 'complements', 'new', 'confirms'];
/** o capítulo do BIS que o achado toca (na construção, os achados vêm agrupados por capítulo) */
const sectionOf = (f: Finding): BisSection | null => f.proposal?.section ?? f.target?.section ?? null;
const FILE_LABEL: Record<string, string> = { image: 'imagem', audio: 'áudio', video: 'vídeo', document: 'documento', other: 'arquivo' };
export const refState = (r: Pick<RefRow, 'status' | 'open' | 'diverges' | 'waiting' | 'applied'>) =>
  r.status === 'reading' ? 'lendo o arquivo' : r.status === 'thinking' ? 'comparando com o BIS' : r.status === 'failed' ? 'não terminou' : r.status === 'skipped' ? 'não deu para ler'
    : [r.diverges && `${r.diverges} ${r.diverges === 1 ? 'diverge' : 'divergem'}`, r.open - r.diverges > 0 && `${r.open - r.diverges} a decidir`, r.waiting && 'tem pergunta', !r.open && r.applied && `${r.applied} no BIS`].filter(Boolean).join(' · ') || 'tudo decidido';

/** O bloco proposto num achado como rascunho do editor (o mesmo formato de um bloco da cópia de trabalho). */
const draftFromFinding = (f: Finding): BlockDraft => draftOf({ ...f.proposal!, id: '', status: 'proposed', generatedBy: 'ai', basis: null, state: 'proposed' } as BisItem);

/**
 * A sala de um documento de referência, dentro do modo construir do BIS. À esquerda os achados da Machine, um por um, já
 * como bloco: o que diverge (com o bloco de hoje ao lado), o que complementa, o que é novo, o que só confirma. À direita a
 * conversa: a Machine diz o que achou e pergunta o que não tem como saber; a resposta refina os achados. Aceitar grava na
 * cópia de trabalho; dispensar pede o porquê, que a Machine leva em conta.
 */
export function ReferenceRoom({ organizationId, id, canEdit, onBis, onClose, onRemoved, build = false }: { organizationId: string; id: string; canEdit: boolean; onBis: () => void; onClose?: () => void; onRemoved?: () => void; build?: boolean }) {
  const q = useQuery({ queryKey: ['bis-reference', id], queryFn: () => api.get<Ref>(`/bis-references/${id}`), refetchInterval: (x) => (x.state.data && ['reading', 'thinking'].includes(x.state.data.status) ? 2500 : false) });
  const r = q.data;
  const refresh = () => void q.refetch();
  const [filter, setFilter] = useState<'open' | 'decided'>('open');
  const [text, setText] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const say = useMutation({ mutationFn: (t: string) => api.post(`/bis-references/${id}/messages`, { text: t }), onSuccess: () => { setText(''); refresh(); } });
  const retry = useMutation({ mutationFn: () => api.post(`/bis-references/${id}/retry`), onSuccess: refresh });
  const remove = useMutation({ mutationFn: () => api.delete(`/bis-references/${id}`), onSuccess: onRemoved });
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [r?.messages.length, r?.status]);
  // a resposta da Machine pode ter acabado de mudar o BIS por baixo (nada muda sozinho, mas o que a pessoa aceitou sim)
  const lastStatus = useRef<BisRefStatus | null>(null);
  useEffect(() => { if (lastStatus.current === 'thinking' && r?.status === 'ready') onBis(); lastStatus.current = r?.status ?? null; }, [r?.status, onBis]);

  const transcribe = async (audio: Blob) => {
    setTranscribing(true); setVoiceError(null);
    try {
      const form = new FormData();
      form.append('audio', audio, 'fala.webm');
      const t = await api.post<{ text: string }>(`/organizations/${organizationId}/rooms/transcribe`, form);
      setText((v) => (v.trim() ? `${v.trim()} ${t.text}` : t.text));
    } catch (e) { setVoiceError((e as Error).message); } finally { setTranscribing(false); }
  };

  if (!r) return <div className="rr-bisref"><p className="rr-faint" style={{ padding: 'var(--rr-space-6)' }}>{q.isError ? (q.error as Error).message : 'carregando…'}</p></div>;
  const busy = r.status === 'reading' || r.status === 'thinking';
  const open = r.findings.filter((f) => f.state === 'open');
  const decided = r.findings.filter((f) => f.state !== 'open');
  const rank = (f: Finding) => (build ? (sectionOf(f) ? BIS_SECTIONS.indexOf(sectionOf(f)!) + 1 : 0) * 10 : 0) + KIND_ORDER.indexOf(f.kind);
  const shown = (filter === 'open' ? open : decided).slice().sort((a, b) => rank(a) - rank(b));
  const lastQuestions = r.messages.at(-1)?.role === 'machine' ? r.messages.at(-1)?.questions ?? [] : [];
  const more = r.messages.at(-1)?.role === 'machine' && Boolean(r.messages.at(-1)?.more);

  return (
    <div className="rr-bisref">
      <main className="rr-bisref__main">
        <header>
          <div>
            <Label>{build ? 'construção do BIS · o material das áreas do cliente' : `documento de referência · ${FILE_LABEL[r.kind] ?? 'arquivo'}`}</Label>
            <h2>{build ? 'o que as áreas dizem, capítulo por capítulo' : r.name}</h2>
            {r.summary && <p><Serif italic>{r.summary}</Serif></p>}
            <small className="rr-faint">{build ? `aberta ${formatDate(r.createdAt)}${r.by ? ` por ${r.by}` : ''}` : `subido ${formatDate(r.createdAt)}${r.by ? ` por ${r.by}` : ''}`}{r.truncated ? ` · ${build ? 'o material' : 'o documento'} é longo: a Machine leu só o começo` : ''}</small>
          </div>
          {!build && <div className="rr-bisref__headactions">
            <button type="button" className="rr-action rr-action--bare" onClick={onClose}>voltar aos capítulos</button>
            {canEdit && <button type="button" className="rr-action rr-action--bare" disabled={remove.isPending} onClick={() => { if (window.confirm('tirar este documento? o que já foi levado ao BIS continua lá.')) remove.mutate(); }}>tirar o documento</button>}
          </div>}
        </header>

        {(r.status === 'failed' || r.status === 'skipped') && (
          <Notice tone="error">{r.error ?? 'a leitura não terminou.'} {canEdit && r.status === 'failed' && <button type="button" className="rr-action" disabled={retry.isPending} onClick={() => retry.mutate()}>tentar de novo</button>}</Notice>
        )}
        {r.status === 'reading' && <Thinking label="lendo o arquivo" steps={[{ kind: 'stage', text: r.kind === 'audio' || r.kind === 'video' ? 'transcrevendo a fala e olhando o que se vê' : r.kind === 'image' ? 'descrevendo a imagem e o texto que aparece nela' : 'extraindo o texto do documento' }]} lines={3} />}
        {r.status === 'thinking' && !r.findings.length && <Thinking label={build ? 'lendo o material das áreas' : 'comparando com o BIS'} steps={[{ kind: 'stage', text: build ? 'juntando o que cada área diz, capítulo por capítulo, e onde as áreas não dizem a mesma coisa' : 'procurando o que diverge, o que complementa, o que é novo e o que confirma' }]} lines={4} />}

        {r.findings.length > 0 && (
          <div className="rr-bisref__tabs">
            <button type="button" className={cx(filter === 'open' && 'is-on')} onClick={() => setFilter('open')}>em aberto · {open.length}</button>
            <button type="button" className={cx(filter === 'decided' && 'is-on')} onClick={() => setFilter('decided')}>decididos · {decided.length}</button>
          </div>
        )}
        {r.status === 'ready' && filter === 'open' && !open.length && r.findings.length > 0 && <p className="rr-faint">nada em aberto: tudo o que a Machine achou já foi decidido. pergunte na conversa se quiser que ela olhe de novo por outro ângulo.</p>}
        {r.status === 'ready' && !r.findings.length && <p className="rr-faint">{build ? 'a Machine não achou no material nada que mude o BIS. a conversa ao lado diz o que falta.' : 'a Machine não achou nada neste documento que mude o BIS. a conversa ao lado diz por quê.'}</p>}
        {shown.map((f, n) => {
          const sec = sectionOf(f);
          const head = build && (n === 0 || sectionOf(shown[n - 1]!) !== sec);
          return (
            <div key={f.key} className="rr-stack rr-stack--3">
              {head && <h3 className="rr-bisref__chapter">{sec ? BIS_SECTION_LABELS[sec] : 'assuntos a decidir'}</h3>}
              <FindingCard f={f} refId={id} canEdit={canEdit} busy={busy} onDone={() => { refresh(); onBis(); }} onAnswer={(t) => setText((v) => (v.trim() ? `${v.trim()}\n${t}` : t))} />
            </div>
          );
        })}
      </main>

      <aside className="rr-bisref__talk">
        <Label>conversa com a Machine</Label>
        <div className="rr-bisref__turns">
          {r.messages.map((m, n) => (
            <Turn key={n} who={m.role === 'person' ? 'me' : 'system'}>
              <div className="rr-bisref__say">
                {m.role === 'person' && m.byName && <small>{m.byName}</small>}
                <p>{m.text}</p>
                {m.questions && m.questions.length > 0 && (
                  <ol>{m.questions.map((x, k) => <li key={k}>{x}</li>)}</ol>
                )}
              </div>
            </Turn>
          ))}
          {r.status === 'thinking' && r.findings.length > 0 && <Thinking label="respondendo" steps={[{ kind: 'stage', text: build ? 'relendo o material com o que você disse e refinando os achados' : 'relendo o documento com o que você disse e refinando os achados' }]} lines={2} />}
          <div ref={end} />
        </div>
        {canEdit && r.read && (
          <>
            {more && !busy && <button type="button" className="rr-action" disabled={say.isPending} onClick={() => say.mutate('Mostre os próximos achados.')}>mostrar os próximos achados</button>}
            {lastQuestions.length > 0 && !busy && <small className="rr-faint">responda do seu jeito, uma ou todas de uma vez.</small>}
            <AskField value={text} onChange={setText} onSubmit={() => { if (text.trim()) say.mutate(text.trim()); }} disabled={busy || say.isPending}
              placeholder={lastQuestions.length ? 'responda às perguntas, ou diga o que mais olhar' : 'pergunte, corrija ou peça para olhar algo específico'}
              onAudio={transcribe} transcribing={transcribing} onAudioError={setVoiceError} />
            {(say.error || voiceError) && <small className="rr-bisref__err">{voiceError ?? (say.error as Error).message}</small>}
          </>
        )}
      </aside>
    </div>
  );
}

function FindingCard({ f, refId, canEdit, busy, onDone, onAnswer }: { f: Finding; refId: string; canEdit: boolean; busy: boolean; onDone: () => void; onAnswer: (prefill: string) => void }) {
  const [edit, setEdit] = useState<BlockDraft | null>(null);
  const [why, setWhy] = useState<string | null>(null);
  const apply = useMutation({ mutationFn: (x: { block?: BlockDraft; asNew?: boolean }) => api.post(`/bis-references/${refId}/findings/${f.key}/apply`, { ...(x.block ? { block: (({ basis: _b, ...b }) => b)(sendBlock(x.block)) } : {}), ...(x.asNew ? { asNew: true } : {}) }), onSuccess: () => { setEdit(null); onDone(); } });
  const dismiss = useMutation({ mutationFn: (note: string) => api.post(`/bis-references/${refId}/findings/${f.key}/dismiss`, { note }), onSuccess: () => { setWhy(null); onDone(); } });
  const reopen = useMutation({ mutationFn: () => api.post(`/bis-references/${refId}/findings/${f.key}/reopen`), onSuccess: onDone });
  const p = f.proposal;
  const pending = apply.isPending || dismiss.isPending;
  const error = ((apply.error ?? dismiss.error) as Error | null)?.message;
  const onTarget = f.kind === 'diverges' || f.kind === 'complements';

  return (
    <article className={cx('rr-bisref__finding', `rr-bisref__finding--${f.kind}`, f.state !== 'open' && 'is-decided')}>
      <div className="rr-bisref__findhead">
        <Tag tone={f.kind === 'diverges' || f.kind === 'conflict' ? 'accent' : 'outline'}>{BIS_REF_FINDING_LABELS[f.kind]}</Tag>
        <b>{p?.title ?? f.topic ?? f.target?.title ?? f.targetTitle}</b>
        <span className="rr-faint">
          {p ? `entra em ${BIS_SECTION_LABELS[p.section]} · ${BIS_BLOCK_LABELS[p.kind]}` : ''}
          {onTarget && (f.target ? ` · ${f.kind === 'diverges' ? 'reescreve' : 'soma ao'} “${f.target.title}”` : ' · o bloco de hoje não está mais no BIS: entra como novo')}
          {f.kind === 'new' && ' · bloco novo'}
        </span>
      </div>
      {f.quote && <blockquote className="rr-bisref__quote"><Serif italic>“{f.quote}”</Serif>{f.where && <small>{f.where}</small>}</blockquote>}
      {f.reason && <p className="rr-bisref__reason">{f.reason}</p>}
      {f.kind === 'conflict' && f.sides && (
        <div className="rr-bisref__sides">
          {f.sides.map((x, k) => <div key={k}><Label>{x.area}</Label><p><Serif italic>{x.says}</Serif></p></div>)}
        </div>
      )}

      {edit ? (
        <div className="rr-bisref__edit">
          <BlockEditor d={edit} onChange={setEdit} onSave={() => apply.mutate({ block: edit })} onCancel={() => setEdit(null)} busy={apply.isPending} error={error ?? null} />
        </div>
      ) : f.kind === 'diverges' && f.target && p ? (
        <div className="rr-bisref__compare">
          <div><Label>hoje no BIS</Label><BlockView block={f.target} compact /></div>
          <div><Label tone="accent">com o documento</Label><BlockView block={p} compact /></div>
        </div>
      ) : p ? (
        <div className="rr-bisref__block"><BlockView block={p} compact /></div>
      ) : f.target ? (
        <div className="rr-bisref__block rr-bisref__block--quiet"><BlockView block={f.target} compact /></div>
      ) : null}

      {f.state !== 'open' ? (
        <div className="rr-bis__meta">
          <span>{f.state === 'applied' ? 'levado ao BIS' : 'dispensado'}{f.decidedByName ? ` por ${f.decidedByName}` : ''}{f.decidedAt ? `, ${formatDate(f.decidedAt)}` : ''}{f.decidedNote ? `: ${f.decidedNote}` : ''}</span>
          {canEdit && f.state === 'dismissed' && <button type="button" className="rr-action rr-action--bare" disabled={reopen.isPending} onClick={() => reopen.mutate()}>voltar para em aberto</button>}
        </div>
      ) : canEdit && f.kind === 'conflict' ? (
        <div className="rr-bis__actions">
          <button type="button" className="rr-action" disabled={busy} onClick={() => onAnswer(`Sobre ${f.topic ?? 'este assunto'}: `)}>decidir na conversa</button>
          <button type="button" className="rr-action rr-action--bare" disabled={pending || busy} onClick={() => setWhy('')}>não é conflito</button>
          {why !== null && (
            <div className="rr-bisref__why">
              <Textarea rows={2} value={why} onChange={(e) => setWhy(e.target.value)} placeholder="por quê? (a Machine leva em conta)" autoFocus />
              <div className="rr-bis__actions"><button type="button" className="rr-action" disabled={pending} onClick={() => dismiss.mutate(why)}>tirar da lista</button><button type="button" className="rr-action rr-action--bare" onClick={() => setWhy(null)}>cancelar</button></div>
            </div>
          )}
        </div>
      ) : canEdit && f.kind !== 'confirms' && p && !edit && (
        why !== null ? (
          <div className="rr-bisref__why">
            <Textarea rows={2} value={why} onChange={(e) => setWhy(e.target.value)} placeholder="por que não entra? (a Machine leva em conta: “é material de 2019”, “isso é só da campanha de verão”)" autoFocus />
            <div className="rr-bis__actions">
              <button type="button" className="rr-action" disabled={pending} onClick={() => dismiss.mutate(why)}>dispensar</button>
              <button type="button" className="rr-action rr-action--bare" onClick={() => setWhy(null)}>cancelar</button>
            </div>
          </div>
        ) : (
          <div className="rr-bis__actions">
            <button type="button" className="rr-action" disabled={pending || busy} onClick={() => apply.mutate({})}>{f.kind === 'diverges' ? 'trocar pelo do documento' : f.kind === 'complements' ? 'somar ao bloco' : 'levar ao BIS'}</button>
            <button type="button" className="rr-action rr-action--bare" disabled={pending || busy} onClick={() => { apply.reset(); setEdit(draftFromFinding(f)); }}>ajustar antes</button>
            {onTarget && f.target && <button type="button" className="rr-action rr-action--bare" disabled={pending || busy} onClick={() => apply.mutate({ asNew: true })}>entrar como bloco à parte</button>}
            <button type="button" className="rr-action rr-action--bare" disabled={pending || busy} onClick={() => setWhy('')}>{f.kind === 'diverges' ? 'manter o de hoje' : 'dispensar'}</button>
          </div>
        )
      )}
      {error && !edit && <small className="rr-bisref__err">{error}</small>}
      {f.kind === 'confirms' && <small className="rr-faint">só confirma: não muda o BIS.</small>}
    </article>
  );
}
